import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car as CarIcon, Search as SearchIcon } from 'lucide-react';
import { Car, CarFilters } from '../types/car';
import { getCars, getMakes, getBatterySizes, triggerCarSync } from '../api/cars';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Loading } from '../components/common/Loading';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useDebounce } from '../hooks/useDebounce';
import { ROUTES } from '../utils/constants';

/**
 * Cars Page Component
 * Displays car-battery compatibility information with search and filtering
 */
export const CarsPage = () => {
  const navigate = useNavigate();
  const makeDropdownRef = useRef<HTMLDivElement>(null);
  const [cars, setCars] = useState<Car[]>([]);
  const [makes, setMakes] = useState<string[]>([]);
  const [batterySizes, setBatterySizes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [makeSearchQuery, setMakeSearchQuery] = useState('');
  const [showMakeDropdown, setShowMakeDropdown] = useState(false);
  
  // Filters
  // Filters
  const [filters, setFilters] = useState<CarFilters>({
    page: 1,
    limit: 50,
    search: '',
    make: '',
    model: '',
    batterySize: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  // Debounce text input filters to prevent losing focus while typing
  const debouncedSearch = useDebounce(filters.search, 500);
  const debouncedModel = useDebounce(filters.model, 500);
  const debouncedYear = useDebounce(filters.year, 500);

  // Load makes and battery sizes on mount
  useEffect(() => {
    loadMakes();
    loadBatterySizes();
  }, []);

  // Close make dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (makeDropdownRef.current && !makeDropdownRef.current.contains(event.target as Node)) {
        setShowMakeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load cars when debounced filters or immediate filters (make, batterySize, page, limit) change
  useEffect(() => {
    loadCars();
  }, [debouncedSearch, debouncedModel, debouncedYear, filters.make, filters.batterySize, filters.page, filters.limit]);
  const loadMakes = async () => {
    try {
      const data = await getMakes();
      setMakes(data);
    } catch (err) {
      console.error('Failed to load makes:', err);
    }
  };

  const loadBatterySizes = async () => {
    try {
      const data = await getBatterySizes();
      setBatterySizes(data);
    } catch (err) {
      console.error('Failed to load battery sizes:', err);
    }
  };

  const loadCars = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use debounced values for text inputs to prevent API calls on every keystroke
      const filtersToSend: CarFilters = {
        ...filters,
        search: debouncedSearch,
        model: debouncedModel,
        year: debouncedYear
        // batterySize is already in filters and doesn't need debouncing (it's a dropdown)
      };
      
      const response = await getCars(filtersToSend);
      setCars(response.data.cars);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load cars');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await triggerCarSync();
      alert('تم بدء مزامنة بيانات السيارات. قد يستغرق هذا بضع لحظات.');
      setTimeout(() => {
        loadCars();
      }, 3000);
    } catch (err: any) {
      alert(err.response?.data?.error?.message || 'فشل بدء المزامنة');
    } finally {
      setSyncing(false);
    }
  };

  const handleFilterChange = (key: keyof CarFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleMakeSelect = (make: string) => {
    handleFilterChange('make', make);
    setMakeSearchQuery(make);
    setShowMakeDropdown(false);
  };

  const filteredMakes = makes.filter(make =>
    make.toLowerCase().includes(makeSearchQuery.toLowerCase())
  );

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 50,
      search: '',
      make: '',
      model: '',
      year: undefined,
      batterySize: ''
    });
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleBatteryClick = (batterySize: string) => {
    // Navigate to search page with battery size as query
    navigate(`${ROUTES.SEARCH}?battery=${encodeURIComponent(batterySize)}`);
  };

  if (loading && cars.length === 0) {
    return <Loading text="جاري تحميل بيانات السيارات..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-primary">دليل السيارات والبطاريات</h1>
        <Button
          onClick={handleSync}
          disabled={syncing}
          variant="secondary"
        >
          {syncing ? 'جاري المزامنة...' : 'مزامنة من Google Sheets'}
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <Input
              type="text"
              placeholder="بحث عن الماركة أو الموديل..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon={<SearchIcon className="h-5 w-5" />}
            />
          </div>

          <div className="relative" ref={makeDropdownRef}>
            <Input
              type="text"
              placeholder="جميع الماركات..."
              value={makeSearchQuery}
              onChange={(e) => {
                setMakeSearchQuery(e.target.value);
                setShowMakeDropdown(true);
                if (!e.target.value) {
                  handleFilterChange('make', '');
                }
              }}
              onFocus={() => setShowMakeDropdown(true)}
            />
            {showMakeDropdown && filteredMakes.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-dark-lighter border border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <button
                  onClick={() => {
                    handleFilterChange('make', '');
                    setMakeSearchQuery('');
                    setShowMakeDropdown(false);
                  }}
                  className="w-full text-right px-4 py-2 hover:bg-dark text-gray-400 hover:text-gray-200 transition-colors border-b border-gray-700"
                >
                  جميع الماركات
                </button>
                {filteredMakes.map((make) => (
                  <button
                    key={make}
                    onClick={() => handleMakeSelect(make)}
                    className="w-full text-right px-4 py-2 hover:bg-dark text-gray-200 hover:text-primary transition-colors"
                  >
                    {make}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Input
              type="text"
              placeholder="اسم الموديل..."
              value={filters.model || ''}
              onChange={(e) => handleFilterChange('model', e.target.value)}
            />
          </div>

          <div>
            <Input
              type="number"
              placeholder="السنة (مثال: 2022)"
              value={filters.year || ''}
              onChange={(e) => handleFilterChange('year', e.target.value ? parseInt(e.target.value) : '')}
              min="1900"
              max="2100"
            />
          </div>

          <div>
            <select
              value={filters.batterySize || ''}
              onChange={(e) => handleFilterChange('batterySize', e.target.value)}
              className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
            >
              <option value="">جميع أحجام البطاريات</option>
              {batterySizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            عرض {cars.length} من {pagination.total} سيارة
            {makes.length === 0 && pagination.total === 0 && (
              <span className="ml-2 text-yellow-500">
                (لا توجد بيانات - اضغط على "مزامنة من Google Sheets")
              </span>
            )}
          </div>
          {(filters.search || filters.make || filters.model || filters.year || filters.batterySize) && (
            <Button
              onClick={handleClearFilters}
              variant="secondary"
              size="sm"
            >
              مسح الفلاتر
            </Button>
          )}
        </div>
      </div>

      {/* Cars Table */}
      {cars.length === 0 && !loading ? (
        <div className="card text-center py-12">
          <CarIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg font-semibold">
            {pagination.total === 0 ? 'لا توجد سيارات في قاعدة البيانات' : 'لا توجد سيارات مطابقة للفلاتر المحددة'}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {pagination.total === 0
              ? 'اضغط على زر "مزامنة من Google Sheets" أعلاه لاستيراد السيارات'
              : 'جرب مسح الفلاتر للعرض جميع السيارات'}
          </p>
          {(filters.search || filters.make || filters.model || filters.year || filters.batterySize) && pagination.total > 0 && (
            <Button
              onClick={handleClearFilters}
              variant="primary"
              size="sm"
              className="mt-4"
            >
              مسح جميع الفلاتر
            </Button>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>الماركة</th>
                  <th>الموديل</th>
                  <th>السنة</th>
                  <th>البطاريات المتوافقة</th>
                </tr>
              </thead>
              <tbody>
                {cars.map((car) => (
                  <tr key={car.id}>
                    <td className="font-semibold">{car.make}</td>
                    <td className="font-medium">{car.model}</td>
                    <td className="text-gray-400">{car.year}</td>
                    <td>
                      <div className="flex flex-wrap gap-2">
                        {car.batteries.map((battery) => (
                          <button
                            key={battery.id}
                            onClick={() => handleBatteryClick(battery.batterySize)}
                            className="badge badge-primary cursor-pointer hover:bg-primary/80 transition-colors"
                            title={`البحث عن بطارية ${battery.batterySize}`}
                          >
                            {battery.batterySize}
                            {battery.count > 1 && (
                              <span className="mr-1 text-xs opacity-75">
                                ({battery.count})
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-700 pt-4">
              <div className="text-sm text-gray-400">
                صفحة {pagination.page} من {pagination.pages}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  variant="secondary"
                  size="sm"
                >
                  السابق
                </Button>
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.pages - 2) {
                    pageNum = pagination.pages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      variant={pagination.page === pageNum ? 'primary' : 'secondary'}
                      size="sm"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  variant="secondary"
                  size="sm"
                >
                  التالي
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CarsPage;
